import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';

import moment from 'moment';

import type { APIData, DChartInterface, DSSetup, Service } from 'webapp-types';
import { AnnotationType } from 'webapp/enums/enums';
import { TrackingEvent } from 'webapp/enums/tracking';
import { normalizeMetricSetupData, serializeMetricSetupData } from 'webapp/utils/common';
import { parseMarkdownToHtml } from 'webapp/utils/html';

import MetricSetup from 'webapp/models-custom/metric-setup';
import Serviced from 'webapp/models-custom/serviced';

export interface AnnotationAnomalyExtra {
	metricAnomalyId: number;
	upperValue: number;
	lowerValue: number;
	score: number;
}

export interface AnnotationData {
	id: number | null;
	timestamp: number;
	note: string;
	metricSetup: DSSetup.MetricSetupData | null;
	user?: {
		id: number;
		name: string;
		avatar: string;
	};
	type: AnnotationType;
	extra?: AnnotationAnomalyExtra;
}

export interface DataPointInfo {
	timestamp: number;
	metricSetupData?: DSSetup.MetricSetupData | null;
}

export default class Annotation extends Serviced {
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('infolayer') declare infolayerService: Service.Infolayer;
	@service('request') declare requestService: Service.Request;
	@service('model-manager/sources') declare mmSourcesService: Service.ModelManager.Sources;
	@service('model-manager/metric-anomalies')
	declare metricAnomaliesService: Service.ModelManager.MetricAnomalies;

	@tracked id: AnnotationData['id'] = null;
	@tracked timestamp: AnnotationData['timestamp'];
	@tracked note: AnnotationData['note'] = '';
	@tracked metricSetup: MetricSetup;
	@tracked user: AnnotationData['user'] = undefined;
	@tracked type: AnnotationData['type'] = AnnotationType.DEFAULT;
	@tracked extra: AnnotationData['extra'] = undefined;

	dataPointInfo: DataPointInfo;
	timezone: string;

	constructor(model: AnnotationData | null, dataPointInfo: DataPointInfo, timezone: string) {
		super();

		if (model) {
			this.id = model.id;
			this.timestamp = model.timestamp;
			this.note = model.note;
			this.user = model.user;
			this.type = model.type;
			this.extra = model.extra;
		} else {
			this.timestamp = dataPointInfo.timestamp;
		}

		const metricSetupData = model?.metricSetup
			? normalizeMetricSetupData(model.metricSetup)
			: null;

		const metricSetup = new MetricSetup(metricSetupData || undefined);
		this.metricSetup = metricSetup;

		this.dataPointInfo = dataPointInfo;
		this.timezone = timezone || this.mmSourcesService.defaultTimezone;
	}

	get isAnomalyType() {
		return this.type === AnnotationType.ANOMALY;
	}

	get isNew() {
		return !this.id;
	}

	get displayName() {
		return this.user?.name || 'Unknown user';
	}

	get displayTime() {
		let time = null,
			format = 'MMM D, YYYY';

		if (this.timezone && this.timestamp) {
			const momentObj = moment.unix(this.timestamp).tz(this.timezone);

			if (momentObj.hours() !== 0) {
				format += ' [at] hh:mm A';
			}

			time = momentObj.format(format);
		}

		return time;
	}

	get noteTrimmed() {
		return this.note.trim();
	}

	get noteFormatted() {
		return parseMarkdownToHtml({
			text: this.note,
		});
	}

	get singleMetricOnly() {
		return !!this.metricSetup.metricSetupData;
	}

	set singleMetricOnly(value) {
		const emptyMetricSetupData = {
			key: '',
			spaceAccessId: null,
		};

		const metricSetupData = this.dataPointInfo.metricSetupData || emptyMetricSetupData;

		this.metricSetup.setFromMetricSetupData(value ? metricSetupData : emptyMetricSetupData);
	}

	save() {
		const isNew = this.isNew,
			note = this.noteTrimmed,
			layer = this.infolayerService.loading('Saving annotation...');

		const annotation: APIData.Annotation.SaveRequest['annotation'] = {
			note,
			timestamp: this.timestamp,
		};

		if (!isNew) {
			annotation.id = this.id;
		}

		if (this.singleMetricOnly) {
			annotation.metricSetup = serializeMetricSetupData(this.metricSetup.metricSetupData);
		}

		const context = Annotation.getContext();

		return new Promise((resolve, reject) => {
			this.requestService
				.api<APIData.Annotation.SaveResponse>(
					isNew ? 'POST' : 'PUT',
					'/metrics/annotations' + (isNew ? '' : '/' + this.id),
					{ annotation, context } satisfies APIData.Annotation.SaveRequest,
				)
				.then(
					(reply) => {
						layer.ok('Annotation saved!');

						if (isNew) {
							this.analyticsService.event(TrackingEvent.ANNOTATION_CREATED);
						}

						if (reply.annotation) {
							this.id = reply.annotation.id;

							// TODO: remove currentUser fallback once BE returns user object in POST response
							// https://app.asana.com/1/7582066485656/inbox/1208538735386092/item/1213977555044235/story/1214099002896917
							if (reply.annotation.user) {
								this.user = reply.annotation.user;
							} else if (
								isNew &&
								reply.annotation.userId === this.currentUserService.numericId
							) {
								const currentUser = this.currentUserService.model;
								if (currentUser && currentUser.numericId !== null) {
									this.user = {
										id: currentUser.numericId,
										name: currentUser.name,
										avatar: currentUser.avatar,
									};
								}
							}
						}

						resolve(reply);
					},
					(errorData) => {
						layer.close();
						reject(errorData);
					},
				);
		});
	}

	delete() {
		const layer = this.infolayerService.loading('Deleting annotation...'),
			context = Annotation.getContext();

		return new Promise((resolve, reject) => {
			this.requestService.api('DELETE', '/metrics/annotations/' + this.id, { context }).then(
				() => {
					layer.ok('Annotation deleted!');
					const title = this.metricSetup.metric?.name || '';
					if (title) {
						const metricAnomalyId = this.extra?.metricAnomalyId;

						let metricAnomaly = null;
						if (metricAnomalyId) {
							metricAnomaly = this.metricAnomaliesService.findModel(
								String(metricAnomalyId),
							);
						}

						this.metricAnomaliesService.sendTrackingEventForDeleteAnomalyAnnotation(
							title,
							metricAnomaly?.settings,
						);
					}
					resolve(null);
				},
				(errorData) => {
					layer.close();
					reject(errorData);
				},
			);
		});
	}

	toJSON() {
		return {
			id: this.id,
			timestamp: this.timestamp,
			note: this.note,
			metricSetup: this.metricSetup.metricSetupData,
			user: this.user,
			type: this.type,
		};
	}

	static _context: DChartInterface.AnnotationContext | null = null; // we send this on POST, PUT & DELETE endpoints to prioritize sample generation

	static setContext(context: DChartInterface.AnnotationContext) {
		this._context = context;
	}

	static getContext() {
		return this._context;
	}

	static createFromModel(model: AnnotationData, timezone: string) {
		const dataPointInfo: DataPointInfo = {
			timestamp: model.timestamp,
			metricSetupData: model.metricSetup,
		};

		return new Annotation(model, dataPointInfo, timezone);
	}

	static createNew(dataPointInfo: DataPointInfo, timezone: string) {
		return new Annotation(null, dataPointInfo, timezone);
	}
}
